// Chat API service for communicating with the Spring Boot backend

import type { ChatRequest, ChatResponse, StreamingEvent, FeedbackRequest, FeedbackResponse, ChatConfigResponse } from '../types/chat';
import { authService } from './authService';
import { decodeJwtPayload } from '../utils/jwt';

const API_CONFIG = {
  // Use environment variables for different environments
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
  chatEndpoint: '/api/chat',
  streamEndpoint: '/api/chat/stream',
  suggestionsEndpoint: '/api/chat/suggestions',
  configEndpoint: '/api/chat/config'
};

// SSE Connection Configuration
const SSE_CONFIG = {
  TOKEN_EXPIRATION_THRESHOLD_MS: 5 * 60 * 1000, // 5 minutes
  RETRY_DELAY_MS: 1000,  // 1 second delay before retry
  MAX_RETRIES: 1,  // Single retry attempt to avoid hammering backend
} as const;

// Retryable error patterns for connection reset detection
const RETRYABLE_ERROR_PATTERNS = [
  /Connection reset/i,
  /recvAddress/i,
  /Connection.*lost/i,
  /net::ERR_CONNECTION_RESET/i,  // Chrome
  /NetworkError/i,  // Firefox
] as const;

/**
 * Check if error matches any retryable pattern
 */
function isRetryableError(errorData: unknown): boolean {
  if (typeof errorData !== 'string') return false;
  return RETRYABLE_ERROR_PATTERNS.some(pattern => pattern.test(errorData));
}

export class ChatApiService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_CONFIG.baseUrl;
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = authService.getToken();
    if (token && authService.isAuthenticated()) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Send a regular (non-streaming) chat request
   */
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    const response = await fetch(`${this.baseUrl}${API_CONFIG.chatEndpoint}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        // Token expired or invalid, redirect to login
        authService.removeToken();
        window.location.href = '/';
        return Promise.reject(new Error('Authentication required'));
      }
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Send a streaming chat request using Server-Sent Events
   * @param retryCount - Internal parameter tracking retry depth (not exposed to callers)
   * @returns Cleanup function that cancels the connection
   */
  streamMessage(
    request: ChatRequest,
    onEvent: (event: StreamingEvent) => void,
    onError: (error: Error) => void,
    onComplete: () => void,
    retryCount: number = 0
  ): () => void {
    const token = authService.getToken();

    if (!token || !authService.isAuthenticated()) {
      onError(new Error('Authentication required'));
      return () => {};
    }

    // Validate token expiration before establishing SSE connection
    // This prevents connection failures after tab inactivity
    try {
      const payload = decodeJwtPayload(token);
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();

      // Check if token is expired or about to expire
      if (expiresAt - now < SSE_CONFIG.TOKEN_EXPIRATION_THRESHOLD_MS) {
        console.warn('Token expired or expiring soon, redirecting to login');
        onError(new Error('Session expired. Please login again.'));
        authService.removeToken();
        window.location.href = '/';
        return () => {};
      }
    } catch (e) {
      console.error('Failed to validate token:', e);
      onError(new Error('Invalid authentication token'));
      authService.removeToken();
      return () => {};
    }

    const params = new URLSearchParams({
      question: request.question
    });

    // Add conversationId if provided
    if (request.conversationId) {
      params.append('conversationId', request.conversationId);
    }

    // Add token as query parameter for Server-Sent Events
    params.append('token', token);

    const eventSource = new EventSource(
      `${this.baseUrl}${API_CONFIG.streamEndpoint}?${params.toString()}`
    );

    // Track if we've completed to prevent unwanted reconnections
    let hasCompleted = false;
    // Track the last error message from the error event listener
    let lastErrorMessage: string | null = null;
    // Track cleanup function from retried connection
    let retryCleanup: (() => void) | null = null;
    // Track connection ID for stop endpoint
    let connectionId: number | null = null;
    // Track if we've received any chunks (to detect grace period scenario)
    let hasReceivedChunks = false;

    // Alternative approach using POST (commented out for now)
    // This would require a different backend implementation
    /*
    const streamWithPost = () => {
      fetch(`${this.baseUrl}${API_CONFIG.streamEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(request),
      }).then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body reader available');
        }

        const decoder = new TextDecoder();

        const readStream = (): Promise<void> => {
          return reader.read().then(({ done, value }) => {
            if (done) {
              onComplete();
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('event:')) {
                const event = line.slice(6).trim() as StreamingEvent['event'];
                continue;
              }
              if (line.startsWith('data:')) {
                const data = line.slice(5).trim();
                try {
                  const parsedData = data.startsWith('[') || data.startsWith('{')
                    ? JSON.parse(data)
                    : data;
                  onEvent({ event: 'chunk', data: parsedData });
                } catch (e) {
                  onEvent({ event: 'chunk', data });
                }
              }
            }

            return readStream();
          });
        };

        return readStream();
      }).catch(onError);
    };
    */

    // Set up event listeners for different event types
    eventSource.addEventListener('connection-id', (event) => {
      // Capture connection ID for stop endpoint
      connectionId = parseInt(event.data, 10);
    });

    eventSource.addEventListener('status', (event) => {
      onEvent({
        event: 'status',
        data: event.data
      });
    });

    eventSource.addEventListener('chunk', (event) => {
      // Mark that we've started receiving chunks
      hasReceivedChunks = true;

      // Remove quotes added by backend to preserve spaces
      let chunkData = event.data;
      if (chunkData.startsWith('"') && chunkData.endsWith('"') && chunkData.length >= 2) {
        chunkData = chunkData.slice(1, -1);
      }
      onEvent({
        event: 'chunk',
        data: chunkData
      });
    });

    eventSource.addEventListener('tool-call', (event) => {
      onEvent({
        event: 'tool-call',
        data: event.data
      });
    });

    // Listen for tool-calls event (array of tool calls for admin users)
    eventSource.addEventListener('tool-calls', (event) => {
      try {
        const toolCalls = JSON.parse(event.data);
        onEvent({
          event: 'tool-calls',
          data: toolCalls
        });
      } catch (error) {
        console.error('Failed to parse tool-calls data:', error);
      }
    });

    // Listen for suggestions event (array of follow-up suggestions)
    eventSource.addEventListener('suggestions', (event) => {
      try {
        const suggestions = JSON.parse(event.data);
        onEvent({
          event: 'suggestions',
          data: suggestions
        });
      } catch (error) {
        console.error('Failed to parse suggestions data:', error);
      }
    });

    eventSource.addEventListener('complete', (event) => {
      if (hasCompleted) return;
      hasCompleted = true;

      onEvent({
        event: 'complete',
        data: event.data
      });
      onComplete();
      // Close the connection to prevent reconnection attempts
      eventSource.close();
    });

    eventSource.addEventListener('error', (event: Event) => {
      // Capture error message for retry logic in onerror handler
      const messageEvent = event as MessageEvent;
      if (messageEvent.data && typeof messageEvent.data === 'string') {
        lastErrorMessage = messageEvent.data;
      }

      // Only send error events if we have actual error data from backend
      // Connection errors (no data) are handled by onerror handler
      if (messageEvent.data) {
        onEvent({
          event: 'error',
          data: messageEvent.data
        });
      }
    });

    eventSource.onerror = (event) => {
      // Don't handle errors if we've already completed
      if (hasCompleted) return;

      // PRIORITY 1: Check if this is a grace period scenario (connection closed during active streaming)
      // If we've received chunks, any error is treated as a disconnect that activates grace period
      // Note: We can't reliably check readyState here due to timing - it updates AFTER error handlers run
      if (hasReceivedChunks) {
        console.info('🔵 Connection closed during streaming - grace period activated on backend');
        hasCompleted = true;
        eventSource.close();

        // Send a special system message to indicate grace period
        onEvent({
          event: 'grace-period',
          data: 'Connection timed out, but the response is still being generated in the background. Please wait, the page will automatically refresh when complete.'
        });
        onComplete();
        return;
      }

      // PRIORITY 2: Check if we should retry for connection errors (before streaming started)
      // Check EventSource readyState to determine if connection can be retried
      // readyState 2 (CLOSED) indicates a connection error
      const eventTarget = event.target as EventSource;
      const isConnectionError = eventTarget.readyState === EventSource.CLOSED;

      const shouldRetry = isConnectionError &&
                         retryCount < SSE_CONFIG.MAX_RETRIES &&
                         (lastErrorMessage ? isRetryableError(lastErrorMessage) : true);

      // Retry for connection reset errors if we haven't exceeded retry limit
      if (shouldRetry) {
        console.warn(`Connection reset detected (attempt ${retryCount + 1}/${SSE_CONFIG.MAX_RETRIES}), retrying SSE connection in ${SSE_CONFIG.RETRY_DELAY_MS}ms...`);
        eventSource.close();

        // Retry after delay to allow browser/network to stabilize
        setTimeout(() => {
          if (!hasCompleted) {
            console.info('Retrying SSE connection...');
            // Recursively call streamMessage with incremented retry count
            // Store cleanup function so caller can cancel retried connection
            retryCleanup = this.streamMessage(request, onEvent, onError, onComplete, retryCount + 1);
          }
        }, SSE_CONFIG.RETRY_DELAY_MS);
        return;
      }

      // PRIORITY 3: Handle other errors (show error message to user)
      // Mark as completed to prevent duplicate error handling
      hasCompleted = true;
      eventSource.close();

      // Show error message to user
      console.error('EventSource error:', event);
      onError(new Error('Connection error occurred. Please try again.'));
      onComplete();
    };

    // Return cleanup function that handles both current and retried connections
    return () => {
      // Call stop endpoint if we have a connection ID
      if (connectionId !== null) {
        fetch(`${this.baseUrl}/api/chat/stop?connectionId=${connectionId}`, {
          method: 'POST',
          headers: this.getAuthHeaders()
        }).catch(err => console.warn('Failed to send stop signal:', err));
      }

      eventSource.close();
      // Also close any retried connection
      if (retryCleanup) {
        retryCleanup();
      }
    };
  }

  /**
   * Get chat configuration
   */
  async getConfig(): Promise<ChatConfigResponse> {
    const response = await fetch(`${this.baseUrl}${API_CONFIG.configEndpoint}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch chat config: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get chat suggestions
   */
  async getSuggestions(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}${API_CONFIG.suggestionsEndpoint}`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // Handle both 401 Unauthorized and 403 Forbidden
          if (response.status === 401) {
            authService.removeToken();
          }
          // Return fallback suggestions for unauthenticated users
          return this.getFallbackSuggestions();
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      return response.json();
    } catch (error) {
      console.warn('Failed to fetch suggestions:', error);
      return this.getFallbackSuggestions();
    }
  }

  private getFallbackSuggestions(): string[] {
    return [
      "What can you do for me?",
      "What can you tell me about LEGO set 76386?",
      "Show me Harry Potter themed sets",
      "Find sets with trans-clear parts",
      "What are the largest LEGO sets?",
      "Tell me about Creator Expert sets"
    ];
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/actuator/health`);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Submit feedback for a message
   */
  async submitFeedback(request: FeedbackRequest): Promise<FeedbackResponse> {
    const response = await fetch(`${this.baseUrl}/api/feedback`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        authService.removeToken();
        window.location.href = '/';
        return Promise.reject(new Error('Authentication required'));
      }
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get feedback for a specific message
   */
  async getFeedback(messageId: string): Promise<FeedbackResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/feedback/${messageId}`, {
        headers: this.getAuthHeaders(),
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          authService.removeToken();
          window.location.href = '/';
          return Promise.reject(new Error('Authentication required'));
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      return null;
    }
  }

  /**
   * Get all feedback for a conversation
   */
  async getConversationFeedback(conversationId: string): Promise<Record<string, FeedbackResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/feedback/conversation/${conversationId}`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          authService.removeToken();
          window.location.href = '/';
          return Promise.reject(new Error('Authentication required'));
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const feedbackList: FeedbackResponse[] = await response.json();

      // Convert array to map keyed by messageId for easy lookup
      const feedbackMap: Record<string, FeedbackResponse> = {};
      feedbackList.forEach(feedback => {
        feedbackMap[feedback.messageId] = feedback;
      });

      return feedbackMap;
    } catch (error) {
      console.error('Failed to fetch conversation feedback:', error);
      return {};
    }
  }
}

// Export singleton instance
export const chatApi = new ChatApiService();
